import { estado } from "../estado";
import { MemoriaLocal } from "../memoria";
import { esc, LOGO, CANDADO, toast, primerNombre } from "../ui";
import { ir } from "../router";
import type { Perfil } from "../api";
import type { Vista } from "./tipos";

type Paso = {
  id: string; pregunta: (p: Perfil) => string; chips: (p: Perfil) => string[];
  libre?: boolean; multi?: boolean; guardar: (r: string | string[], p: Perfil) => void;
};

// El guion de bienvenida de Makai (portado de design/app/makai.js).
export function pasosOnboarding(): Paso[] {
  const u = estado.usuario()!; const a = estado.accesos();
  const nombre = primerNombre(u.nombre);
  const misClientes = a.clientes.map((c) => ({ id: c.id, t: c.nombre }));
  const chat = estado.tenant?.nombreChat || "Makai";
  return [
    { id: "apodo",
      pregunta: () => "Hola, " + nombre + ". Soy " + chat + ", el cerebro de la agencia. Antes de trabajar juntos quiero conocerte un poco: lo que me cuentes queda en una memoria que es solo tuya, y la voy a leer cada vez que me hables.\n\nPrimero lo fácil: ¿cómo quieres que te llame?",
      chips: () => [nombre, u.nombre, "Otro"], libre: true,
      guardar: (r, p) => { p.apodo = String(r); } },
    { id: "trabajo",
      pregunta: (p) => "Perfecto, " + p.apodo + ". Tu rol en la plataforma dice “" + u.rol + "”. Pero eso es la etiqueta: ¿qué haces de verdad en el día a día?",
      chips: () => ["Coordino cuentas y clientes", "Asigno y reviso trabajo del equipo", "Produzco piezas", "Cierro números y estimados", "Investigo y sintetizo"], libre: true,
      guardar: (r, p) => { p.trabajo = String(r); p.rol = u.rol; } },
    { id: "foco",
      pregunta: () => misClientes.length ? "¿En cuáles de tus cuentas pasas más tiempo? Solo te muestro las que tienes asignadas; las demás no existen para mí." : "Todavía no tienes clientes asignados. ¿Con qué tipo de cuentas esperas trabajar?",
      chips: () => misClientes.length ? misClientes.map((c) => c.t) : ["Consumo masivo", "Banca y seguros", "Salud", "Retail"], multi: true, libre: !misClientes.length,
      guardar: (r, p) => { p.foco = (Array.isArray(r) ? r : [r]).map((t) => (misClientes.find((c) => c.t === t) || { id: t }).id); } },
    { id: "estilo", pregunta: () => "¿Cómo prefieres que te responda?", chips: () => ["Corto y al grano", "Con contexto y fuentes", "Depende: pregúntame antes"], guardar: (r, p) => { p.estilo = String(r); } },
    { id: "formato", pregunta: () => "¿Y en qué formato te resulta más cómodo leer?", chips: () => ["Viñetas", "Párrafos", "Tablas cuando haya números"], guardar: (r, p) => { p.formato = String(r); } },
    { id: "horas",
      pregunta: () => "Toda hora de la agencia se imputa a un job. ¿Cuándo prefieres cargar las tuyas? Te lo voy a recordar en ese momento y no en otro.",
      chips: () => ["Al cerrar cada tarea", "Al final del día", "Los viernes", "Recuérdamelo tú"], guardar: (r, p) => { p.horasCuando = String(r); } },
    { id: "quitaTiempo",
      pregunta: () => "¿Qué es lo que más tiempo te quita hoy? Es lo primero que voy a intentar quitarte de encima.",
      chips: () => ["Perseguir briefs incompletos", "Saber quién está libre para asignar", "Rondas de revisión que no avanzan", "Llenar el timesheet", "Buscar documentos viejos del cliente"], libre: true,
      guardar: (r, p) => { p.quitaTiempo = String(r); } },
    { id: "evitar",
      pregunta: () => "Última: ¿hay algo que nunca deba hacer o proponerte?",
      chips: () => ["No me propongas humor para cuentas de salud", "No me interrumpas antes de las 10", "No me resumas: dame el documento", "Nada por ahora"], libre: true,
      guardar: (r, p) => { p.evitar = r === "Nada por ahora" ? "" : String(r); } },
  ];
}

export function resumenPerfil(p: Perfil): [string, string][] {
  return [
    ["Te llamo", p.apodo || ""], ["Lo que haces", p.trabajo || ""], ["Cuentas de foco", (p.foco || []).map((id) => estado.nombreCliente(id) === "—" ? id : estado.nombreCliente(id)).join(", ") || "—"],
    ["Estilo", p.estilo || ""], ["Formato", p.formato || ""], ["Horas", p.horasCuando || ""], ["Te quita tiempo", p.quitaTiempo || "—"], ["Evitar", p.evitar || "—"],
  ];
}

export function onboarding(): Vista {
  const u = estado.usuario()!;
  const pasos = pasosOnboarding();
  const perfil: Perfil = { apodo: primerNombre(u.nombre) };
  let idx = 0; let seleccion: string[] = [];
  const chat = estado.tenant?.nombreChat || "Makai";

  return {
    full:
      '<div class="onb"><div class="chat">' +
        '<div class="atadura"><div class="logo" style="width:22px;height:22px;border-radius:6px">' + LOGO(12) + '</div><span style="font-weight:640;color:var(--text)">' + esc(chat) + '</span> · conversación de bienvenida <span style="margin-left:auto;font-size:11.5px;color:var(--text3)" id="ob-progreso"></span></div>' +
        '<div class="mensajes" id="ob-msgs"></div>' +
        '<div class="compositor"><div class="sugs" id="ob-chips"></div><div class="caja"><input id="ob-input" placeholder="Escribe tu respuesta…"><button class="enviar" id="ob-enviar"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5"/><path d="M5.5 11.5L12 5l6.5 6.5"/></svg></button></div></div>' +
      "</div>" +
      '<div class="panel"><div><div class="tit">Tu memoria, en construcción</div><div class="sub">Esto es lo que ' + esc(chat) + ' va guardando. Es solo tuyo: nadie más de la agencia lo lee, y puedes editarlo o borrarlo cuando quieras.</div></div><div id="ob-memoria"></div>' +
        '<div class="aviso warn" style="margin-top:auto">' + CANDADO + " <b>Lo que " + esc(chat) + " no te va a preguntar</b><br>Nada sobre otros clientes ni sobre cómo está organizada la agencia. Las cuentas que ves son las que ya tienes asignadas.</div></div>" +
      "</div>",
    despues(root) {
      const msgs = root.querySelector<HTMLElement>("#ob-msgs")!; const chips = root.querySelector<HTMLElement>("#ob-chips")!; const input = root.querySelector<HTMLInputElement>("#ob-input")!;
      const pintarMemoria = () => {
        root.querySelector("#ob-memoria")!.innerHTML = '<div class="card" style="padding:4px 0">' + resumenPerfil(perfil).map((f) => '<div class="hecho"><div class="k">' + esc(f[0]) + '</div><div class="v" style="color:' + (f[1] && f[1] !== "—" ? "var(--text)" : "var(--text4)") + '">' + esc(f[1] || "—") + "</div></div>").join("") + "</div>";
        root.querySelector("#ob-progreso")!.textContent = Math.min(idx, pasos.length) + " de " + pasos.length;
      };
      const burbujaMakai = (texto: string, extraHtml?: string) => {
        const d = document.createElement("div"); d.className = "msg";
        d.innerHTML = '<div class="avatar ai">' + LOGO(14) + '</div><div class="cuerpo"><div class="texto">' + esc(texto) + "</div>" + (extraHtml || "") + "</div>";
        msgs.appendChild(d); msgs.scrollTop = msgs.scrollHeight; return d;
      };
      const burbujaYo = (texto: string) => { const d = document.createElement("div"); d.className = "msg yo"; d.innerHTML = '<div class="burbuja">' + esc(texto) + "</div>"; msgs.appendChild(d); msgs.scrollTop = msgs.scrollHeight; };
      const escribiendo = () => { const d = document.createElement("div"); d.className = "msg"; d.innerHTML = '<div class="avatar ai">' + LOGO(14) + '</div><div class="escribiendo"><span></span><span></span><span></span></div>'; msgs.appendChild(d); msgs.scrollTop = msgs.scrollHeight; return d; };

      const preguntar = () => {
        pintarMemoria();
        if (idx >= pasos.length) return resumen();
        const paso = pasos[idx]!; seleccion = [];
        const w = escribiendo();
        setTimeout(() => {
          w.remove(); burbujaMakai(paso.pregunta(perfil));
          chips.innerHTML = paso.chips(perfil).map((c) => '<div class="chip" data-c="' + esc(c) + '">' + esc(c) + "</div>").join("") + (paso.multi ? '<button class="btn primario chico" id="ob-listo">Listo</button>' : "");
          chips.querySelectorAll<HTMLElement>(".chip").forEach((ch) => ch.onclick = () => {
            const v = ch.dataset.c!;
            if (paso.multi) { ch.classList.toggle("on"); seleccion = [...chips.querySelectorAll<HTMLElement>(".chip.on")].map((x) => x.dataset.c!); return; }
            if (v === "Otro") { input.focus(); input.placeholder = "Escríbelo aquí…"; return; }
            responder(v);
          });
          const listo = chips.querySelector<HTMLButtonElement>("#ob-listo"); if (listo) listo.onclick = () => { if (seleccion.length) responder(seleccion); };
          input.disabled = !(paso.libre || paso.multi); input.placeholder = paso.libre ? "Escribe tu respuesta…" : paso.multi ? "Escoge arriba y pulsa Listo" : "Escoge una opción arriba";
          if (paso.libre) input.focus();
        }, 550);
      };
      const responder = (r: string | string[]) => {
        const paso = pasos[idx]!;
        burbujaYo(Array.isArray(r) ? r.join(", ") : r);
        paso.guardar(r, perfil); chips.innerHTML = ""; input.value = ""; idx += 1;
        setTimeout(preguntar, 250);
      };
      const resumen = () => {
        const w = escribiendo();
        setTimeout(() => {
          w.remove();
          burbujaMakai("Gracias, " + perfil.apodo + ". Esto es lo que guardé. Si algo no está bien, cámbialo desde Mi memoria después de entrar: la memoria es tuya, yo solo la leo.",
            '<div class="acciones-msg"><button class="btn primario" id="ob-guardar">Guardar mi memoria y entrar</button><button class="btn" id="ob-repetir">Volver a empezar</button></div>');
          msgs.querySelector<HTMLButtonElement>("#ob-guardar")!.onclick = async (e) => {
            const b = e.currentTarget as HTMLButtonElement; b.disabled = true;
            try { await MemoriaLocal.crear(perfil); toast("<b>Memoria creada.</b> " + esc(chat) + " la va a leer en cada conversación."); ir("#/inicio"); }
            catch (ex) { b.disabled = false; toast("<b>No se pudo guardar.</b> " + esc((ex as Error).message)); }
          };
          msgs.querySelector<HTMLButtonElement>("#ob-repetir")!.onclick = () => { idx = 0; msgs.innerHTML = ""; Object.keys(perfil).forEach((k) => { if (k !== "apodo") delete perfil[k]; }); preguntar(); };
          input.disabled = true; input.placeholder = "";
        }, 600);
      };
      const enviar = () => { const v = input.value.trim(); if (!v || idx >= pasos.length) return; responder(pasos[idx]!.multi ? [v] : v); };
      root.querySelector<HTMLButtonElement>("#ob-enviar")!.onclick = enviar; input.onkeydown = (e) => { if (e.key === "Enter") enviar(); };
      preguntar();
    },
  };
}
